import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";

import useSettings from "../../data/useSettings";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ISetting, SettingInputType, SettingType } from "../../models/Setting";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import {
  MAX_TV_LOCATION_TILES,
  parseTvLocationTiles,
  TV_LOCATION_TILES_SETTING_KEY,
} from "../../utils/tvLocationTiles";

// Shown even before the setting exists, which the generic settings table can't do - an absent
// setting has no row to edit there.
export default function TvLocationTilesCard() {
  const { t } = useTranslation("production");
  const queryClient = useQueryClient();
  const { settings, create, update } = useSettings();
  const { saveHandlers } = useDefaultCRUDHandlers(t("TV location tiles"));

  const setting = settings.find((s) => s.key === TV_LOCATION_TILES_SETTING_KEY);
  const current = parseTvLocationTiles(setting?.value);
  const [value, setValue] = useState(String(current));

  useEffect(() => {
    setValue(String(current));
  }, [current]);

  const parsed = Number(value);
  const valid =
    value.trim() !== "" && Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_TV_LOCATION_TILES;
  const changed = valid && parsed !== current;
  const saving = create.isLoading || update.isLoading;

  const save = async () => {
    if (!changed) return;
    if (setting?.id) {
      await update.mutateAsync({ ...setting, value: String(parsed) } as ISetting, saveHandlers);
    } else {
      await create.mutateAsync(
        {
          key: TV_LOCATION_TILES_SETTING_KEY,
          value: String(parsed),
          settingType: SettingType.General,
          inputType: SettingInputType.Number,
          required: false,
        } as ISetting,
        saveHandlers
      );
    }
    // The dashboard reads this through useSingleSetting, which caches for an hour.
    queryClient.invalidateQueries(["settingKey", TV_LOCATION_TILES_SETTING_KEY]);
  };

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">{t("TV location tiles")}</h3>
      <p className="mt-1 text-sm text-gray-500">
        {t("How many yard sections get their own tile along the top of the Production Dashboard's TV Line view. The Total tile still counts every section.")}
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="tv-location-tiles" className="text-sm font-medium text-gray-500">
            {t("Sections shown")}
          </label>
          <input
            id="tv-location-tiles"
            type="number"
            min={0}
            max={MAX_TV_LOCATION_TILES}
            step={1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 block w-28 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <ButtonInput
          label={t("Save")}
          isSubmit={false}
          onClick={save}
          disabled={!changed || saving}
          isLoading={saving}
          classes=""
        />
        {!valid && (
          <span className="pb-2 text-sm text-red-600">
            {t("Enter a whole number from 0 to {{max}}", { max: MAX_TV_LOCATION_TILES })}
          </span>
        )}
      </div>
    </div>
  );
}
